/* @flow */

import type { OrderStatus } from "shop-state/types";

import React, { useEffect } from "react";
import { SuccessView } from "@crossroads/shop-views";
import { useAnalytics } from "context/analytics";
import { useClient } from "entrypoint/shared";
import { OrderData } from "state/data";
import { useData } from "crustate/react";
import { load as loadOrder } from "state/order";
import { useTranslate } from "@awardit/react-use-translate";
import useFormat from "helpers/hooks/use-format";
import { toMinorUnits } from "helpers/utils";
import CheckIcon from "icons/checkmark-success.svg";
import { retrackEventId } from "../../config";
import Cookies from "js-cookie";
import useRetrack from "helpers/retrack";

const Success = (): React$Node => {
  const orderData = useData(OrderData);
  const { formatPrice } = useFormat();
  const analytics = useAnalytics();
  const t = useTranslate();
  const SUCCESS_STATUSES: Set<OrderStatus> = new Set(["processing", "complete", "payment_review"]);
  const retrackCookie = Cookies.get("retrackEvent");
  const retrack = useRetrack();

  useEffect(() => {
    if (orderData.state === "LOADED") {
      if (orderData.data && SUCCESS_STATUSES.has(orderData.data.status)) {
        const data = orderData.data;

        if (retrackCookie && retrackEventId) {
          const orderSum = data.subTotal.exVat; // Order sum should be the actual value of the Order
          const vatSum = data.shipping ?
            data.grandTotal.vat - data.shipping.total.vat : data.grandTotal.vat;
          const discount = Math.abs(data.discountTotal); // Math.abs because of negative value
          const orderTotal = toMinorUnits(orderSum);
          const discountTotal = toMinorUnits(discount);

          retrack.event("addTransaction", {
            event: retrackEventId,
            click: retrackCookie,
            orderNumber: data.id,
            orderSum: orderTotal,
            vatSum: toMinorUnits(vatSum),
            currency: data.currencyCode,
            discountSum: String(discountTotal), // Refunder wants discountSum as a string
          });

          data.items.forEach(item => {
            const product = item.configOption ? item.configOption.product : item.product;

            retrack.event("addProduct", {
              reference: product.sku,
              name: product.name,
              quantity: item.qty,
              price: toMinorUnits(product.price.exVat),
              vat: toMinorUnits(product.price.vat),
            });
          });

          retrack.send();
        }
      }
    }
  }, [orderData, retrackCookie, retrackEventId]);

  return (
    <SuccessView
      hasRegretPurchaseButton
      orderData={orderData}
      useClient={useClient}
      analytics={analytics}
      formatPrice={formatPrice}
      additionalField="CONTACT_INFO"
      loadOrder={loadOrder}
      CheckIcon={<CheckIcon aria-label={t("ICONS.CHECK")} style={{ color: "#3f921b" }} />}
    />
  );
};

export default Success;
